import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Environment } from '../classes/environment';
import { FileDetails, Folder, Group, GroupFolder, MemberGroup } from '../classes/fileInfo';


@Injectable( { providedIn: 'root' } )
export class FileService {


	requestNewFolder$ = new Subject<string>();
	requestFileUpload$ = new Subject<string>();


	constructor(
        private httpClient: HttpClient,
		@Inject('environment') private environment: Environment,
		private matSnackBar: MatSnackBar,
	) {}


	getFolders(): Observable<Folder[]> {

		return this.httpClient.get<Folder[]>( this.environment.backendUrl + 'folder/all' ).pipe( 
			map( folders => {
				if( !folders ) folders = [];
				return folders;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of folders', err );
				this.matSnackBar.open( 'Beim Abrufen der Ordner-Liste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	getGroups(): Observable<Group[]> {

		return this.httpClient.get<Group[]>( this.environment.backendUrl + 'gruppe/all' ).pipe( 
			map( groups => {
				if( !groups ) groups = [];
				return groups;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of groups', err );
				this.matSnackBar.open( 'Beim Abrufen der Gruppe-Liste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	saveGroup( group: Group ): Observable<Group> {
		
		const obs = ( !group.gruppenId )
			? this.httpClient.post<{ verarbeitet: boolean, gruppe: Group }>( this.environment.backendUrl + 'gruppe/new', group )
			: this.httpClient.put<{ verarbeitet: boolean, gruppe: Group }>( this.environment.backendUrl + 'gruppe/update', group );

		return obs.pipe(
			map( response => {
				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( 'Speichern fehlgeschlagen', '', { duration: 5000 } );
					return new Group();
				}
				this.matSnackBar.open( 'Erfolgreiche Speicherung', '', { duration: 5000 } );
				return response.gruppe;
			}),
			catchError(err => {
				console.log( 'An error occurred while saving a group', err );
				this.matSnackBar.open( 'Beim Speichern einer Gruppe ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(new Group());
			})
		);	

	}

	getGroupFolders( groupId: string ): Observable<GroupFolder[]> {
		
		return this.httpClient.get<GroupFolder[]>( this.environment.backendUrl + 'gf/gruppe/' + groupId ).pipe( 
			map( groupFolders => {
				if( !groupFolders ) groupFolders = [];
				return groupFolders;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of GroupFolders', err );
				this.matSnackBar.open( 'Beim Abrufen der GruppeOrdner-Liste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	getFoldersByGroup( groupId: string ): Observable<Folder[]> {

		return this.httpClient.get<Folder[]>( this.environment.backendUrl + 'folder/gruppe/' + groupId ).pipe( 
			map( folders => {
				if( !folders ) folders = [];
				return folders;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of GroupFolders', err );
				this.matSnackBar.open( 'Beim Abrufen der GruppeOrdner-Liste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	setGroupFolders( groupFolders: GroupFolder[] ): Observable<GroupFolder[] | null> {
		
		return this.httpClient.put<{ verarbeitet: boolean, gfs: GroupFolder[] }>( this.environment.backendUrl + 'gf/update/batch', groupFolders ).pipe(
			map( response => {
				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( 'Speichern fehlgeschlagen', '', { duration: 5000 } );
					return null;
				}
				this.matSnackBar.open( 'Erfolgreiche Speicherung', '', { duration: 5000 } );
				return response.gfs;
			}),
			catchError( err => {
				console.log( 'An error occurred while saving GroupFolders', err );
				this.matSnackBar.open( 'Beim Speichern einer Gruppe ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(null);
			})
		);	

	}

	getMemberGroups( memberId: string ): Observable<MemberGroup[]> {
		
		return this.httpClient.get<MemberGroup[]>( this.environment.backendUrl + 'mg/mitglied/' + memberId ).pipe( 
			map( memberGroups => {
				if( !memberGroups ) memberGroups = [];
				return memberGroups;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of MemberGroups', err );
				this.matSnackBar.open( 'Beim Abrufen der MitgliedGruppe-Liste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	setMemberGroups( memberGroups: MemberGroup[] ): Observable<MemberGroup[] | null> {

		return this.httpClient.put<{ verarbeitet: boolean, mgs: MemberGroup[] }>(
			this.environment.backendUrl + 'mg/update/batch',
			memberGroups
		).pipe(
			map( response => {

				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( 'Speichern fehlgeschlagen', '', { duration: 5000 } );
					return null;
				}
				this.matSnackBar.open( 'Erfolgreiche Speicherung', '', { duration: 5000 } );
				return response.mgs;

			}),
			catchError( err => {
				console.log( 'An error occurred while saving MemberGroups', err );
				this.matSnackBar.open( 'Beim Speichern einer Gruppe ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(null);
			})
		);

	}
	
	getFiles(): Observable<FileDetails[]> {

		return this.httpClient.get<FileDetails[]>( this.environment.backendUrl + 'datei/all' ).pipe( 
			map( files => {
				if( !files ) files = [];
				for( const f of files ) f.bCollapsed = true;
				return files;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of files', err );
				this.matSnackBar.open( 'Beim Abrufen der Datei-Liste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	/*
	getFilesPerMember( memberId: string ): Observable<FileDetails[]> {
		
		return this.httpClient.get<FileDetails[]>( this.environment.backendUrl + 'datei/mitglied/' + memberId ).pipe( 
			map( fileDetails => {
				if( !fileDetails ) fileDetails = [];
				return fileDetails;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of files', err );
				this.matSnackBar.open( 'Beim Abrufen der Datein-Liste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);
	}
	*/

	downloadFile( hash: string, fileName: string ) {

		this.httpClient.get(
			this.environment.backendUrl + 'datei/download/' + hash,
			{ responseType: 'blob' }
			// { responseType: 'blob' as 'json', observe: 'response' }
		).subscribe( data => {

			// console.log( "Content-Disposition", data.headers.get('Content-Disposition') );
			console.log( 'download data=', data, data.type, fileName )	// data.headers


			const blob = new Blob( [data], { type: data.type } );

			const file = new File( 
				[blob], 
				fileName, 
				{ type: data.type, lastModified: Date.now() }
			);

			const fileURL = URL.createObjectURL(file);

			const agent = window.navigator.userAgent.toLowerCase();
			console.log( "agent", agent )

			const downloadLink = document.createElement('a');
			downloadLink.target = '_blank';
			downloadLink.href = fileURL;
			downloadLink.download = fileName;
			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);

			// window.open( fileURL, '_blank' );

		});
	
	}


	/*
	downloadFileById( fileId: string, fileName: string ) {

		this.httpClient.get(
			this.environment.backendUrl + 'datei/download?dateiId=' + fileId,
			{ responseType: 'blob' }
		).subscribe( answer => {

			const blob = new Blob( [answer], { type: answer.type } );

			const file = new File( 
				[blob], 
				fileName, 
				{ type: answer.type, lastModified: Date.now() }
			);

			const fileURL = URL.createObjectURL(file);

			const downloadLink = document.createElement('a');
			downloadLink.target = '_blank';
			downloadLink.href = fileURL;
			downloadLink.download = fileName;
			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);


			if(  ( answer.type == "image/jpeg" ) || ( answer.type == "image/gif" )  ) {
				window.open( fileURL, '_blank' );
			}

		});

	}
	*/

	downloadFileById( fileId: string, fileName: string ) {

		this.downloadFileByIdObs( fileId, fileName ).subscribe();

	}

	downloadFileByIdObs( fileId: string, fileName: string ): Observable<boolean> {

		return this.httpClient.get(
			this.environment.backendUrl + 'datei/download?dateiId=' + fileId,
			{ responseType: 'blob' }
		).pipe( 
			
			map( answer => {

				const blob = new Blob( [answer], { type: answer.type } );

				const file = new File( 
					[blob], 
					fileName, 
					{ type: answer.type, lastModified: Date.now() }
				);

				const fileURL = URL.createObjectURL(file);

				const downloadLink = document.createElement('a');
				downloadLink.target = '_blank';
				downloadLink.href = fileURL;
				downloadLink.download = fileName;
				document.body.appendChild(downloadLink);
				downloadLink.click();
				document.body.removeChild(downloadLink);


				const canOpenInline = !!answer.type && (
					answer.type.startsWith('image/') ||
					answer.type === 'application/pdf' ||
					answer.type.startsWith('text/')
				);

				if( canOpenInline ) {
					// allow preview for formats the browser can render without triggering a second download
					window.open( fileURL, '_blank' );
				}

				return true;

			}),
			catchError( e => of(false) )

		);


	}

	deleteUploadedFile( fileId: string ): Observable<boolean> {

		return this.httpClient.delete<{ verarbeitet: boolean }>(
			this.environment.backendUrl + 'file/delete?dateiId=' + fileId
		).pipe(
	
			map( answer => {
				if( !answer || !answer.verarbeitet ) return false;
				return true;
			}),
			catchError( err => {
				this.matSnackBar.open( 'Beim Löschen der Datei ist ein Fehler aufgetreten', 'Fehler' );
				return of(false);
			})

		);
	
	}

	uploadFiles( path: string, files: File[] = [] ): Observable<boolean|null|413> {
	
		const formData = new FormData();
		formData.append( "path", path );

		for( const file of files ) {
			formData.append( 'files', file, file.name );
		}

		return this.httpClient.post<{ fileName: string }[]>(
			this.environment.backendUrl + 'datei/dokumente/upload',
			formData,
		).pipe(
			map( result => {

				if( !result || !result.length || !result[0].fileName ) {
					return false;
				}

				return true;

			}),
			catchError( (error, src) => {
					if( error && error.status && (error.status == 413) ) return of(413 as 413);
					return of(null);
			})
		);

	}



}
